import React, { useEffect, useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingBag, Heart, MagnifyingGlass, List, X, InstagramLogo, TiktokLogo, ShieldCheck, Truck, ArrowsClockwise } from "@phosphor-icons/react";
import { useShop } from "@/context/ShopContext";
import CartDrawer from "@/components/CartDrawer";
import RecentPurchaseToast from "@/components/RecentPurchaseToast";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import SearchDialog from "@/components/SearchDialog";

const LANGS = [["en","EN"],["pt","PT"],["fr","FR"],["es","ES"],["de","DE"],["it","IT"]];
const CURRENCIES = ["EUR","GBP"];

export default function Layout() {
  const { t, i18n } = useTranslation();
  const { cartCount, setCartOpen, currency, setCurrency } = useShop();
  const [mobile, setMobile] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  const changeLang = (l) => { i18n.changeLanguage(l); localStorage.setItem("bm_lang", l); };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      {/* Top announcement bar */}
      <div className="bg-black text-white text-xs tracking-[0.2em] uppercase py-2.5 text-center font-medium" data-testid="announcement-bar">
        Free Shipping over €150 · 30-Day Returns · Secure Payments
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 glass-header" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button className="md:hidden" onClick={()=>setMobile(true)} aria-label="Menu" data-testid="mobile-menu-btn">
            <List size={22} weight="thin"/>
          </button>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium tracking-wide" data-testid="primary-nav">
            <Link to="/shop" data-testid="nav-shop">{t("nav.shop")}</Link>
            <Link to="/shop/handbags" data-testid="nav-handbags">{t("nav.handbags")}</Link>
            <Link to="/shop/wallets" data-testid="nav-wallets">{t("nav.wallets")}</Link>
            <Link to="/about" data-testid="nav-about">{t("nav.about")}</Link>
          </nav>
          <Link to="/" className="font-serif-bm text-2xl md:text-3xl tracking-[0.25em]" data-testid="brand-logo">BOOM MODE</Link>
          <div className="flex items-center gap-3 md:gap-5">
            <select value={i18n.language} onChange={(e)=>changeLang(e.target.value)} className="bg-transparent text-xs tracking-widest uppercase outline-none cursor-pointer hidden sm:block" data-testid="language-select">
              {LANGS.map(([k,v])=> <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={currency} onChange={(e)=>setCurrency(e.target.value)} className="bg-transparent text-xs tracking-widest uppercase outline-none cursor-pointer hidden sm:block" data-testid="currency-select">
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={()=>setSearchOpen(true)} aria-label="Search" data-testid="search-btn"><MagnifyingGlass size={20} weight="thin"/></button>
            <Link to="/wishlist" aria-label="Wishlist" data-testid="wishlist-link"><Heart size={20} weight="thin"/></Link>
            <button onClick={()=>setCartOpen(true)} aria-label="Cart" className="relative" data-testid="cart-btn">
              <ShoppingBag size={20} weight="thin"/>
              {cartCount > 0 && <span className="absolute -top-1 -right-2 bg-black text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="cart-count">{cartCount}</span>}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobile && (
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm" onClick={()=>setMobile(false)} data-testid="mobile-menu">
          <div className="bg-white w-72 h-full p-6 shadow-xl" onClick={e=>e.stopPropagation()}>
            <div className="flex justify-between items-center mb-8">
              <span className="font-serif-bm text-xl tracking-[0.25em]">BOOM MODE</span>
              <button onClick={()=>setMobile(false)}><X size={20}/></button>
            </div>
            <nav className="flex flex-col gap-5 text-base">
              <Link to="/shop" onClick={()=>setMobile(false)}>{t("nav.shop")}</Link>
              <Link to="/shop/handbags" onClick={()=>setMobile(false)}>{t("nav.handbags")}</Link>
              <Link to="/shop/wallets" onClick={()=>setMobile(false)}>{t("nav.wallets")}</Link>
              <Link to="/about" onClick={()=>setMobile(false)}>{t("nav.about")}</Link>
              <Link to="/contact" onClick={()=>setMobile(false)}>{t("nav.contact")}</Link>
            </nav>
            <div className="mt-8 flex gap-3">
              <select value={i18n.language} onChange={(e)=>changeLang(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs">
                {LANGS.map(([k,v])=> <option key={k} value={k}>{v}</option>)}
              </select>
              <select value={currency} onChange={(e)=>setCurrency(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      <main className="min-h-screen"><Outlet /></main>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white mt-24" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <div className="font-serif-bm text-2xl tracking-[0.25em]">BOOM MODE</div>
            <p className="mt-4 text-sm text-white/60 max-w-sm font-light leading-relaxed">{t("footer.brand")}</p>
            <div className="flex gap-3 mt-6">
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="border border-white/20 rounded-full p-2 hover:border-gold transition" data-testid="footer-instagram"><InstagramLogo size={18} weight="thin"/></a>
              <a href="https://tiktok.com" target="_blank" rel="noreferrer" className="border border-white/20 rounded-full p-2 hover:border-gold transition" data-testid="footer-tiktok"><TiktokLogo size={18} weight="thin"/></a>
            </div>
          </div>
          <div>
            <h4 className="eyebrow text-white/60 mb-4">Shop</h4>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/shop/handbags">{t("nav.handbags")}</Link></li>
              <li><Link to="/shop/wallets">{t("nav.wallets")}</Link></li>
              <li><Link to="/shop">{t("nav.shop")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="eyebrow text-white/60 mb-4">Help</h4>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/shipping">{t("footer.shipping")}</Link></li>
              <li><Link to="/returns">{t("footer.returns")}</Link></li>
              <li><Link to="/faq">{t("footer.faq")}</Link></li>
              <li><Link to="/contact">{t("footer.contact")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="eyebrow text-white/60 mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/about">{t("footer.about")}</Link></li>
              <li><Link to="/privacy">{t("footer.privacy")}</Link></li>
              <li><Link to="/terms">{t("footer.terms")}</Link></li>
              <li><Link to="/admin/login" className="text-white/40">Admin</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/40">
            <div>© {new Date().getFullYear()} Boom Mode. {t("footer.rights")}</div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} weight="thin"/> SSL</span>
              <span className="flex items-center gap-1.5"><Truck size={14} weight="thin"/> EU Wide</span>
              <span className="flex items-center gap-1.5"><ArrowsClockwise size={14} weight="thin"/> 30 days</span>
            </div>
          </div>
        </div>
      </footer>

      <CartDrawer />
      <RecentPurchaseToast />
      <ExitIntentPopup />
      <SearchDialog open={searchOpen} setOpen={setSearchOpen} />
    </>
  );
}
